import React, { useEffect, useLayoutEffect, useReducer, useState } from "react";
import { Dialog, DialogTitle, DialogContent, Button, Box, Divider, Stepper, Step, StepLabel, DialogActions, Tooltip } from '@mui/material'
import CloseIcon from "@mui/icons-material/Close";

import './styles.css'
import { lockedReducer } from "../../utils/defaultReducer";
import api from "../../api";
import useNotification from "../Notification";
import WebFormsStep2 from "./step2";
import WebFormsStep1 from "./step1";
import WebFormsStep3 from "./step3";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"

import { initialState as initial_state_step_1 } from "./step1";
import { initialState as initial_state_step_2 } from "./step2";
import { initialState as initial_state_step_3 } from "./step3";
import ConfirmationDialog from "../Dialogs/Confirmation";
import Loading from "../Loading";
import { getSession } from "../../auth";
import { fieldObject, possibleFields, mapCustomFields } from "./step2";

const formInitialState ={
    id: 0,
    ...initial_state_step_1,
    ...initial_state_step_2,
    ...initial_state_step_3
}

const formToState = (data, contactCustomFields, contactEnterpriseCustomFields, journeyCustomFields) => {
    const extraPossibleFields = {
        ...possibleFields,
        ...mapCustomFields("contact", contactCustomFields),
        ...mapCustomFields("contact_enterprise", contactEnterpriseCustomFields),
        ...mapCustomFields("opportunity", journeyCustomFields),
    }

    return {
        ...formInitialState,
        ...data,
        form: Object.fromEntries((data.fields || []).map((field, index) => ([field.name, fieldObject({
            ...extraPossibleFields[field.name],
            state: 'form',
            title: field.label,
            position: field.position,
            props: field.settings || {},
        })])))
    }
}

const validState = (step, state) => {
    const messages = []
    switch(step){
        case 0:
            if (!state.name) messages.push(<span>Nome é um campo obrigatório.</span>)
            if (!state.title) messages.push(<span>Título é um campo obrigatório.</span>)
            break
        case 1:
            break
        case 2:
            break
        default:
            break
    }
    if (messages.length === 0)
        return ''
    return messages
}

const steps = ['Configurações gerais', 'Layout', 'Publicação'];

const EditWebForms = ({ data, journey, handleClose, contactCustomFields, contactEnterpriseCustomFields, journeyCustomFields }) => {
    const [step, setStep] = useState(0)
    const [state, setState] = useReducer(lockedReducer, formInitialState)
    const [confirmation, setConfirmation] = useState(false)
    const [loading, setLoading] = useState(false)

    useLayoutEffect(()=>{
        setState(formToState(data, contactCustomFields, contactEnterpriseCustomFields, journeyCustomFields))
        setStep(data.step || 0)
    }, [])

    const validstate = validState(step, state)

    const uploadLogo = async () => {
            if (!state.logo) return null
            const formData = new FormData()
            formData.append('file', state.logo, (state.logo.name || `logo-${journey.id}`).replace(/\s+/g, '_'))
            
            const res = await api.post(`api/whatsapp/uploadmedia/`, formData, { timeout: 15000 })
            if (res.status === 200)
                return res.data
            return null
        }

    const submit = async () => {
        setLoading(true)
        
        const logo_url = await uploadLogo() 
        const enterpriseId = getSession()?.profile?.enterprise?.id
        
        let hasRequired = false
        const payload = {
            ...state,
            logo_url: logo_url || state.logo_url,
            journey: journey?.id,
            groups: state.groups.map(group => group.id),
            fields: Object.entries(state.form)
            .toSorted((a, b) => a.position - b.position)
            .flatMap(([key, item]) => {
                if (item.state !== 'form') return []
                if (item.props.required) hasRequired = true
                return [{
                    label: item.title,
                    name: key,
                    type: item.type,
                    position: item.position,
                    settings: item.props,
                    contact_custom_field: item.contact_custom_field_id,
                    contact_enterprise_custom_field: item.contact_enterprise_custom_field_id,
                    journey_custom_field: item.journey_custom_field_id
                }]
            })
        }

        if (! hasRequired) {
            useNotification(
                'Atenção!',
                'O formulário precisa ter pelo menos UM campo obrigatório!',
                'warning'
            )
            setLoading(false)
            setConfirmation(false)
            return
        }

        let promise = null
        if (!state.id)
            promise = api.post(`api/enterprises/${enterpriseId}/forms/` , payload)
        else
            promise = api.patch(`api/enterprises/${enterpriseId}/forms/${state.id}/` , payload)

        const res = await promise
        if ([200, 201].includes(res?.status)){
            useNotification(
                'Sucesso!',
                'Formulário publicado!',
                'success'
            )
            setState(formToState({...res.data, logo: null}))
            setStep(p => p+1)
        } 
        else if (res.status === 400) {
            let message = 'Campo obrigatório não foi preenchido'
            if ('title' in res.data)
                message= 'Campo título não foi preenchido'
            useNotification(
                'Ops!',
                message,
                'danger'
            )
        }
        else {
            useNotification(
                'Ops!',
                'Ocorreu um erro para publicar o formulário',
                'danger'
            )
        }
        setLoading(false)
        setConfirmation(false)
    }

    return (
        <>
        <Loading loading={loading}/>
        {confirmation && 
        <ConfirmationDialog 
            open={true}
            handleClose={()=>setConfirmation(false)}
            handleNo={()=>setConfirmation(false)}
            handleSubmit={submit}
            title='Publicar'
            description={'Deseja publicar o formulário?'}
        />
        }
        <Dialog
            open={true}
            onClose={() => { }}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
            fullWidth
            maxWidth="md"
            sx={{ height: '100%' }}
            PaperProps={{ sx: { height: '80vh', maxHeight: 'calc(100% - 64px)' } }}
        >
            <DialogTitle id="alert-dialog-slide-title" sx={{ display: "flex", justifyContent: "space-between" }}>
                {state.id === 0 ? 'Novo' : 'Alterar'} Formulário
                <Box>
                    <Button sx={{ 'minWidth': '2rem', 'padding': '0' }} onClick={handleClose} ><CloseIcon /></Button>
                </Box>
            </DialogTitle >
            <Divider />
            <DialogContent sx={{display:'flex', flexDirection:'column', gap:'1rem', minHeight: 0, overflow: 'hidden'}}>
                <Stepper activeStep={step} alternativeLabel>
                    {steps.map((label, index) => {
                        return (
                            <Step key={label} >
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {step === 0 &&
                        <WebFormsStep1
                            parent={state}
                            setParent={setState}
                        />}
                    {step === 1 &&
                        <WebFormsStep2
                            parent={state}
                            setParent={setState}
                            contactCustomFields={contactCustomFields}
                            contactEnterpriseCustomFields={contactEnterpriseCustomFields}
                            journeyCustomFields={journeyCustomFields}
                        />
                    }
                    {step === 2 && <WebFormsStep3 parent={state} setParent={setState} />}
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                {step > 0 && 
                <Button onClick={() => setStep(p => p-1)}>
                    Anterior
                </Button>
                }   
                {step === 0  && 
                <Tooltip title={validstate} >
                    <span><Button disabled={validstate} onClick={() => setStep(p => p+1)}>
                    Próximo
                </Button></span></Tooltip>
                }
                {step === 1  && 
                <Tooltip title={validstate}><span><Button disabled={validstate} onClick={() => setConfirmation(true)}>
                    Publicar
                </Button></span></Tooltip>
                }
                {step === steps.length-1 && 
                <Tooltip title={validstate}><span><Button disabled={validstate} onClick={handleClose}>
                    Concluir
                </Button></span></Tooltip>
                }   
            </DialogActions>

        </Dialog>
        </>
    )
}

export default EditWebForms